import { useCallback, useState } from 'react';
import { WithConfigurationCanvas } from '../../../configuration-canvas/WithConfigurationCanvas';
import {
  GroupLabelEntityType,
  GroupPillEntityType,
  NodeEntityTypes,
  StepEntityType,
  StepPillEntityType,
} from '../../../../sweep-canvas/canvas-types';
import { PanelConsumer } from '../../../../panels/PanelsContext';
import { usePanels } from '../../../../panels/usePanels';
import { Box } from '@mui/material';
import { useConfigurationCanvas } from '../../../configuration-canvas/public/useConfigurationCanvas';
import { useSelector } from 'react-redux';
import { stagePanelType } from '../../../funnel-map-flow/dialogs/stage/panelType';
import {
  selectStageDialogFunnelId,
  selectStageDialogStage,
} from '../../../../../reducers/funnel-flow-page/stageDialogReducer';
import { gatePanelType } from '../../../funnel-map-flow/dialogs/gates/gatePanelType';
import { StageDialog } from '../../../funnel-map-flow/dialogs/stage/StageDialog';
import { ConnectedGateDialog } from '../../../funnel-map-flow/dialogs/gates/ConnectedGateDialog';
import noop from 'lodash/noop';
import useOpenStepDialog from '../useOpenStageDialog';

export const ReadOnlyCanvasUnitedConfigurationPage = () => {
  const { maybeOpenPanel } = usePanels();
  const { canvasCrmOrgId } = useConfigurationCanvas();
  const { openStepDialog } = useOpenStepDialog();
  const [selectedGateId, setSelectedGateId] = useState<string>();
  const selectedStage = useSelector(selectStageDialogStage);
  const selectedStageDialogFunnelId = useSelector(selectStageDialogFunnelId);
  const { canvasFunnelMap } = useConfigurationCanvas();

  const onEntityClick = useCallback(
    ({
      entity,
      funnelId,
      stepId,
    }: {
      stepId?: string;
      funnelId: string;
      entity: StepEntityType | StepPillEntityType | GroupPillEntityType | GroupLabelEntityType;
      event: React.MouseEvent | React.TouchEvent;
    }) => {
      if (entity.type === NodeEntityTypes.Step && stepId && funnelId) {
        openStepDialog({ stepId, funnelId });
        return;
      }
    },
    [openStepDialog],
  );

  const onStageGateClick = useCallback(
    (funnelId: string, stageId: string, exitCriteriaId: string) => {
      maybeOpenPanel({
        panelType: gatePanelType,
        onOpen: () => {
          setSelectedGateId(exitCriteriaId);
        },
        onClose: () => {
          setSelectedGateId(undefined);
        },
        data: {
          funnelId,
          stageId,
          exitCriteriaId,
        },
      });
    },
    [maybeOpenPanel],
  );

  return (
    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '100%' }}>
      <WithConfigurationCanvas
        visibilityElements={{ type: 'map', visibilityMap: {} }}
        onFunnelStepClick={onEntityClick}
        onFunnelLabelClick={onEntityClick}
        onFunnelLabelPillClick={onEntityClick}
        onStageGateClick={onStageGateClick}
        selectedGateId={selectedGateId}
      >
        {selectedStage && selectedStageDialogFunnelId && (
          <PanelConsumer panelType={stagePanelType} instanceId={selectedStage._stageId}>
            {({ maybeClosePanel, isOpen }) => (
              <>
                {isOpen && (
                  <StageDialog
                    readonly
                    stage={selectedStage}
                    funnelId={selectedStageDialogFunnelId}
                    crmOrgId={canvasCrmOrgId}
                    closeDialog={() => maybeClosePanel()}
                    setIsDirty={noop}
                    funnelMap={canvasFunnelMap}
                  />
                )}
              </>
            )}
          </PanelConsumer>
        )}
        <PanelConsumer panelType={gatePanelType}>
          {({ maybeClosePanel, isOpen, data }) => (
            <>
              {data && (
                <ConnectedGateDialog
                  crmOrgId={canvasCrmOrgId}
                  readonly
                  closeDialog={() => maybeClosePanel()}
                  isOpen={isOpen}
                  data={data}
                  setIsDirty={noop}
                />
              )}
            </>
          )}
        </PanelConsumer>
      </WithConfigurationCanvas>
    </Box>
  );
};
